import { ColumnAggregationType } from "utils/enums";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { T } from "services/lang/LanguageService";
import { SortDirection } from "gql/generated/types";
import { DataTableValue } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import { Row } from "primereact/row";
import useSafeId from "hooks/dashboard/useDashboardComponentId";
import React, { CSSProperties, WheelEvent, useCallback, useEffect, useRef, useState } from "react";
import DataCell from "./components/templates/table-components/data-cell/DataCell";
import AttributeHeader from "./components/templates/table-components/header/AttributeHeader";
import {
  IColumnsWithValuesProps,
  IProductTemplateAttribute,
  ITableDataEntry,
  ProductTemplateNonTransposedTableProps,
} from "./utils/interfaces";

import {
  checkForHighlightType,
  checkForUnwantedType,
  compareValues,
  formatDataTotalsAggregation,
  getHighlightColor,
  getValueColors,
  handlePrecision,
} from "./utils/utils";
import "./ProductTemplateTable.scss";

export default function ProductTemplateTableDefault({
  templateData,
  setTemplateData,
  configData,
  selectedEntryBody,
  selectedEntryId,
  selectedEntryIndex,
  itemsPerPage,
  editedDataHandler,
  onClickSign,
  handleTableKeyDown,
  EditRowDataCell,
  reportOptions,
  editColumnLocation = "BACK",
  page,
  setPage,
  allowRowEdit,
  foreignKeyWrap,
  showTotals,
  hidePagination,
  isColumnsSortable,
  defaultSortingAttribute,
  defaultSorting,
}: ProductTemplateNonTransposedTableProps) {
  const tableId = useSafeId();
  const divRef = useRef<HTMLDivElement>(null);

  const [selected, setSelected] = useState<DataTableValue | null>(null);
  const [rows, setRows] = useState(itemsPerPage);
  const [aggregatedColumns, setAggregatedColumns] = useState<IColumnsWithValuesProps[]>([]);

  const [sortField, setSortField] = useState<string | null>(defaultSortingAttribute ?? null);
  const [sortOrder, setSortOrder] = useState<SortDirection>(defaultSorting ?? SortDirection.Asc);
  const rowsPerPageOptions = itemsPerPage
    ? [itemsPerPage, 10, 20, 30].sort((a, b) => a - b).filter((x, i, a) => a.indexOf(x) === i)
    : [];

  useEffect(() => {
    if (sortField && sortOrder) {
      const sorted = sortTableData(templateData, sortField, sortOrder);
      setTemplateData(sorted);
    }
  }, []);

  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    if (event.altKey) {
      const div: HTMLDivElement = event.currentTarget as HTMLDivElement;
      div.scrollLeft += event.deltaY * 0.45;
    }
  }, []);

  const sortTableData = (
    data: ITableDataEntry[],
    field: string,
    order: SortDirection
  ): ITableDataEntry[] => {
    const direction = order === SortDirection.Asc ? 1 : -1;
    
    return [...data].sort((a, b) => {
      const valA = a.entries[field]?.value;
      const valB = b.entries[field]?.value;
      return direction * compareValues(valA, valB);
    });
  };

  const handleSort = (field: string) => {
    let newOrder = SortDirection.Asc;
    if (sortField === field) {
      if (sortOrder === SortDirection.Asc) {
        newOrder = SortDirection.Desc;
      } else {
        newOrder = SortDirection.Asc;
      }
    }

    setSortField(field);
    setSortOrder(newOrder);
    const sorted = sortTableData(templateData, field, newOrder);
    setTemplateData(sorted);
  };

  const dataCell = (
    val: ITableDataEntry,
    index: number,
    configVal: IProductTemplateAttribute,
    configIndex: number
  ) => {
    const dataValue = selectedEntryBody && selectedEntryIndex === index ? selectedEntryBody : val;
    const obj = dataValue.entries[configVal.referenceName];

    let color;
    let bgColor;
    if (obj && obj.type && checkForHighlightType(obj.type)) {
      [color, bgColor] = getValueColors(obj.value as number, {
        highlightRanges: configVal.bgHighlightRanges,
        defaultHighlightColor: configVal.highlightColor,
      });
    } else {
      [color, bgColor] = getHighlightColor(configVal.highlightColor);
    }

    return (
      <div className="text-center" style={styleHandler(color, bgColor, obj && obj.type)}>
        <DataCell
          key={val.id}
          headerConfig={configVal}
          dataValue={dataValue}
          indexHeader={configIndex}
          indexEntry={index}
          changeHandler={editedDataHandler}
          isInEditMode={selectedEntryId === val.id}
          onClickSign={onClickSign}
          foreignKeyWrap={foreignKeyWrap}
          editRequireRoles={configVal.editRequireRoles ?? []}
        />
      </div>
    );
  };

  const styleHandler = (
    color: string | undefined,
    bgColor: string | undefined,
    type: string | undefined
  ): CSSProperties | undefined => {
    if (checkForUnwantedType(type)) {
      if (color && bgColor) {
        return {
          overflowX: "auto",
          color,
          backgroundColor: bgColor,
        };
      }
      if (bgColor) {
        return {
          overflowX: "auto",
          backgroundColor: bgColor,
        };
      }

      if (color) {
        return {
          overflowX: "auto",
          color,
        };
      }
    }

    return {
      overflowX: "auto",
    };
  };

  const generateFooterColumns = (aggregatedCols: IColumnsWithValuesProps[]) => {
    return aggregatedCols.map(col => {
      let tooltipText = "";
      let footerText: string | number = "";
      if (col.aggregationType === ColumnAggregationType.SUM) {
        tooltipText = T("word.sum");
        footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
      } else if (col.aggregationType === ColumnAggregationType.MEAN) {
        tooltipText = T("word.average");
        footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
      } else if (col.aggregationType === ColumnAggregationType.COUNT) {
        tooltipText = T("action.count");
        footerText = handlePrecision(col.total!, col.decimalPlaces!, true);
      }
      return (
        <Column
          key={col.referenceName}
          footer={
            <div title={tooltipText} className="text-center">
              {footerText}
            </div>
          }
        />
      );
    });
  };

  let footerGroup = null;
  if (showTotals && aggregatedColumns.length > 0) {
    footerGroup = (
      <ColumnGroup>
        <Row>{generateFooterColumns(aggregatedColumns)}</Row>
      </ColumnGroup>
    );
  }

  useEffect(() => {
    if (reportOptions && document.getElementsByClassName("print-mode-enabled").length > 0) {
      const table = document.querySelector(`#${tableId}`);
      if (table) {
        const allChildren = table.querySelectorAll("*");

        if (reportOptions.fontSize) {
          for (let i = 0; i < allChildren.length; i++) {
            allChildren[i].style.fontSize = reportOptions?.fontSize;
          }
        }
      }
    }
  }, [footerGroup]);

  useEffect(() => {
    setAggregatedColumns(formatDataTotalsAggregation(configData, templateData));
  }, [configData?.data?.attributes, templateData]);

  const editFront = editColumnLocation === "FRONT";
  const editBack = editColumnLocation === "BACK";
  const columns = configData?.data?.attributes ?? [];

  const visibleRows = rows ? [...templateData.slice(page, page + rows)] : templateData;

  const renderFooterCells = () => {
    if (!showTotals || aggregatedColumns.length === 0) return null;

    return (
      <tfoot>
        <tr>
          {allowRowEdit && editFront && <td className="non-printable" />}
          {aggregatedColumns.map(col => {
            let tooltipText = "";
            let footerText: string | number = "";

            if (col.aggregationType === ColumnAggregationType.SUM) {
              tooltipText = T("word.sum");
              footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
            } else if (col.aggregationType === ColumnAggregationType.MEAN) {
              tooltipText = T("word.average");
              footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
            } else if (col.aggregationType === ColumnAggregationType.COUNT) {
              tooltipText = T("action.count");
              footerText = handlePrecision(col.total!, col.decimalPlaces!, true);
            }

            return (
              <td key={col.referenceName}>
                <div title={tooltipText} className="text-center">
                  {footerText}
                </div>
              </td>
            );
          })}
          {allowRowEdit && editBack && <td className="non-printable" />}
        </tr>
      </tfoot>
    );
  };

  const renderEditCell = (rowData: ITableDataEntry, rowIndex: number) => (
    <td className="non-printable" style={{ width: 0 }}>
      {EditRowDataCell(rowData, rowIndex)}
    </td>
  );

  const sortIconFor = (referenceName: string) => {
    const isSorted = sortField === referenceName;
    if (!isColumnsSortable) return null;

    let icon = "alt";
    if (isSorted) {
      icon = sortOrder === SortDirection.Asc ? "amount-up-alt" : "amount-down";
    } else {
      icon = "alt";
    }

    return <i className={`pi pi-sort-${icon}`} />;
  };

  return (
    <>
      <div
        className="default-table"
        id={tableId}
        style={{ overflowX: "auto" }}
        onKeyDown={e => handleTableKeyDown(e.key)}
        ref={divRef}
        onWheel={handleWheel}
      >
        <table className="p-datatable p-component" style={{ width: "100%" }}>
          <thead>
            <tr className="table-header">
              {allowRowEdit && editFront && <th className="non-printable" />}
              {columns.map(col => (
                <th
                  key={col.referenceName}
                  onClick={() => isColumnsSortable && handleSort(col.referenceName)}
                  className="table-header-cell"
                  style={{ textAlign: "center", cursor: isColumnsSortable ? "pointer" : "default" }}
                >
                  <div className="flex align-items-center gap-2 justify-content-center ">
                    <AttributeHeader key={col.referenceName} attribute={col} noTh />
                    {sortIconFor(col.referenceName)}
                  </div>
                </th>
              ))}
              {allowRowEdit && editBack && <th className="non-printable" />}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, localIdx) => {
              const absoluteIdx = (page ?? 0) + localIdx;
              const isSelected = selected?.id === row.id;
              return (
                <tr
                  key={row.id}
                  className={isSelected ? "is-selected" : ""}
                  onClick={() => setSelected(row)}
                >
                  {allowRowEdit && editFront && renderEditCell(row, absoluteIdx)}

                  {columns.map((col, i) => (
                    <td
                      key={`${row.id}-${col.referenceName}`}
                      style={{ textAlign: "center", padding: 0 }}
                    >
                      {dataCell(row, absoluteIdx, col, i)}
                    </td>
                  ))}

                  {allowRowEdit && editBack && renderEditCell(row, absoluteIdx)}
                </tr>
              );
            })}
          </tbody>

          {renderFooterCells()}
        </table>
      </div>
      {!!itemsPerPage && !hidePagination && (
        <Paginator
          totalRecords={templateData.length}
          rows={rows}
          first={page}
          onPageChange={e => {
            setPage(e.first);
            setRows(e.rows);
          }}
          template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate={`${T("word.showing")} {first} ${T("word.to")} {last} ${T("word.of")} {totalRecords}`}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </>
  );
}
